import '../loading/gradient';
import '../loading/style';

import { Component, tag } from 'omi';

import classname, { getClassPrefix } from '../_util/classname';
import { getChildrenArray } from '../_util/component';
import { setExportparts } from '../_util/dom';
import eventDispose from '../_util/eventDispose';
import { flexIcon } from '../_util/icon';
import { convertToLightDomNode } from '../_util/lightDom';
import parseTNode from '../_util/parseTNode';
import { StyledProps } from '../common';
import { TdButtonProps } from './type';

export interface ButtonProps extends TdButtonProps, StyledProps {}

@tag('t-button')
export default class Button extends Component<ButtonProps> {
  static css = [];

  static propTypes = {
    children: [Function, Object, String, Number],
    theme: String,
    type: String,
    variant: String,
    size: String,
    shape: String,
    icon: Object,
    loading: Boolean,
    ghost: Boolean,
    block: Boolean,
    disabled: Boolean,
    href: String,
    tag: String,
    content: [String, Object],
    onClick: Function,
    ignoreAttributes: Array,
    innerStyle: String,
  };

  static defaultProps = {
    tag: 'button',
    variant: 'base',
    size: 'medium',
    shape: 'rectangle',
    loading: false,
    ghost: false,
    disabled: false,
    block: false,
    ignoreAttributes: [],
  };

  get tag() {
    const { tag, href, disabled, loading } = this.props;
    const isDisabled = disabled || loading;
    if (!tag && href && !isDisabled) return 'a';
    if (!tag && isDisabled) return 'div';
    return tag || 'button';
  }

  get theme() {
    const { theme, variant } = this.props;
    if (!theme) {
      if (variant === 'base') return 'primary';
      return 'default';
    }
    return theme;
  }

  ready() {
    setExportparts(this);
  }

  clickHandle = (e: MouseEvent) => {
    eventDispose.call(this, 'click', e, () => {
      const { disabled, loading } = this.props;
      if (disabled || loading) return false;
      return true;
    });
  };

  render(props: ButtonProps) {
    const {
      icon,
      variant,
      size,
      block,
      disabled,
      ghost,
      loading,
      shape,
      ignoreAttributes,
      suffix,
      innerClass,
      innerStyle,
      children,
      content,
      ...rest
    } = props;

    delete rest.onClick;
    delete rest.className;
    delete rest.style;

    const classPrefix = getClassPrefix();
    const isDisabled = disabled || loading;

    if (ignoreAttributes?.length > 0) {
      ignoreAttributes.forEach((attr) => {
        this.removeAttribute(attr);
      });
    }

    const iconNode = convertToLightDomNode(flexIcon(icon));

    let loadingNode = null;
    if (loading) {
      loadingNode = convertToLightDomNode(
        flexIcon(<t-loading-gradient className={`${classPrefix}-loading ${classPrefix}-loading--inherit-color`} />),
      );
    }

    // 判断是否有文字内容
    const childrenArray = getChildrenArray(children);
    const hasTextContent = childrenArray.some((child) => {
      if (typeof child === 'string' || typeof child === 'number') {
        return true;
      }
      if (typeof child === 'object' && child !== null && 'attributes' in child) {
        return (child as any).attributes?.slot !== 'icon';
      }
      return !!child;
    });
    const hasContent = !!(hasTextContent || content);

    const renderIconSlot = () => {
      if (loading) {
        return loadingNode;
      }
      if (hasContent && icon) {
        return (
          <span className={`${classPrefix}-icon`}>
            <slot name="icon">{iconNode}</slot>
          </span>
        );
      }
      return <slot name="icon">{iconNode}</slot>;
    };

    const Tag = this.tag as string;
    return (
      <Tag
        className={classname(
          [
            `${classPrefix}-button`,
            `${classPrefix}-button--theme-${this.theme}`,
            `${classPrefix}-button--variant-${variant}`,
          ],
          {
            [`${classPrefix}-button--shape-${shape}`]: shape !== 'rectangle',
            [`${classPrefix}-button--ghost`]: ghost,
            [`${classPrefix}-is-loading`]: loading,
            [`${classPrefix}-is-disabled`]: isDisabled,
            [`${classPrefix}-size-s`]: size === 'small',
            [`${classPrefix}-size-l`]: size === 'large',
            [`${classPrefix}-size-full-width`]: block,
          },
          innerClass,
        )}
        part={`${classPrefix}-button`}
        onClick={this.clickHandle}
        style={innerStyle}
        disabled={isDisabled || undefined}
        {...rest}
      >
        {renderIconSlot()}
        <span className={`${classPrefix}-button__text`} part={`${classPrefix}-button__text`}>
          <slot></slot>
        </span>
        {suffix && (
          <span className={`${classPrefix}-button__suffix`} part={`${classPrefix}-button__suffix`}>
            {parseTNode(suffix)}
          </span>
        )}
      </Tag>
    );
  }
}
